import { VARIANT_A } from '../experiments/experimentDefaults';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { FlowManager } from '../flow-management/FlowManager';
import { setNextStep } from '../flow-management/setNextStep';
import { onOAuthExistingUser } from '../google-authentication/onOAuthExistingUser';
import Logger from '../lib/Logger';
import { Data } from '../signup-data/DATA';
import { setIsNewUser, setVerificationType } from '../store/app/action-creators';
import { newOAuthUser } from '../store/auth/action-creators';
import { getIsActiveAndAssignedToVariant } from '../store/experiments/selectors';
import { OAuthExistingUserErrorMessage } from '../store/oauth/action-creators';
import { OAuthActionTypes } from '../store/oauth/action-types';
import { bulkUpdate, setSignupDataValue } from '../store/signup-data/action-creators';
import { Tracker } from '../store/tracking/action-creators';
import { Step } from '../views/Step';
import { OAuthToDataToken } from './OAuthMapping';
import { onOAuthVerifyError } from './onOAuthVerifyError';
import { getOAuthVerificationType } from './utils/getOAuthVerificationType';
export const LOG = Logger.getLogger('OAuthUtilsActions');
/**
 * Triggering Actions:
 * - OAUTH_AUTH_SUCCEED
 * - VERIFICATION_FAILED
 * - VERIFICATION_SUCCEEDED
 * @description: handles the response from the OAuth Signup buttons.
 * If successful, there are 2 cases:
 * 1. 200 status with user already exists message - means the email is attached to an existing user. Adds the existing user step.
 * 2. updates data values in the store, adds and removes steps based on
 * what data values we received.
 * If unsuccessful:
 * 1. Failure - Logs and dispatches interactions. Doesn't change the step.
 */

export const OAuthMiddleware = store => next => action => {
  const state = store.getState();

  if (action.type === OAuthActionTypes.AuthSucceeded) {
    const {
      token
    } = action.payload;
    store.dispatch(setSignupDataValue(OAuthToDataToken[action.provider], token));
  }

  if (action.type === OAuthActionTypes.VerificationFailed) {
    const isExistingUser = action.payload.status === OAuthExistingUserErrorMessage;

    if (isExistingUser) {
      store.dispatch(onOAuthExistingUser(state, action.provider));
    } else {
      store.dispatch(onOAuthVerifyError(action.payload, action.provider));
    }
  }

  if (action.type === OAuthActionTypes.VerificationSucceeded) {
    const {
      email,
      firstName,
      lastName,
      companyDomain,
      emailVerificationToken
    } = action.payload;
    store.dispatch(setVerificationType(getOAuthVerificationType(action.provider)));
    store.dispatch(newOAuthUser(action.provider));
    store.dispatch(setIsNewUser(true));
    const someUserInformationIsMissing = !email || !firstName || !lastName;

    if (someUserInformationIsMissing) {
      store.dispatch(Tracker.signupInteraction('oauth-success-empty', {
        provider: action.provider
      }));
    }

    const maybeOverrideHubSpotCompanyDomainInternal = companyDomain === 'hubspot.com' && {
      [Data.CompanyDomain]: 'hubspot-test.com'
    };
    const dataToUpdate = Object.assign({
      [Data.Email]: email,
      [Data.FirstName]: firstName || '',
      [Data.LastName]: lastName || '',
      [Data.CompanyDomain]: companyDomain || '',
      [Data.Token]: emailVerificationToken
    }, maybeOverrideHubSpotCompanyDomainInternal);
    store.dispatch(bulkUpdate(dataToUpdate));
    store.dispatch({
      type: OAuthActionTypes.AuthDataStored,
      provider: action.provider
    });
    const isAssignedToACQ126 = getIsActiveAndAssignedToVariant(state, ExperimentKeys.ACQ126, [VARIANT_A]);
    const stepsToRemove = [...(firstName ? [Step.FirstName] : []), ...(lastName ? [Step.LastName] : []), ...(email ? [Step.Email] : []), Step.Verification, Step.CodeVerification, Step.Password, Step.Name, Step.EmailOnlyLandingPage];
    const nextStepsToAdd = [...(!email ? [Step.Email] : []), ...(!firstName ? [Step.FirstName] : []), ...(!lastName ? [Step.LastName] : [])];
    const acq126NextStepsToAdd = [Step.OnePageSignupGoogle];
    store.dispatch(setNextStep({
      rebuilder: isAssignedToACQ126 ? FlowManager.pushNextAndRemove(acq126NextStepsToAdd, stepsToRemove) : FlowManager.pushNextAndRemove(nextStepsToAdd, stepsToRemove)
    }));
  }

  return next(action);
};